import {
  useCallback,
  useState,
  type FocusEvent,
  type KeyboardEvent,
  type ReactElement,
  type RefObject,
} from "react";
import { clsx } from "clsx";
import { Layers, X } from "lucide-react";
import { Avatar } from "@primitives/Avatar";
import { Text } from "@primitives/Text";
import { TextInput } from "@primitives/TextInput";
import type { QueueSummary } from "../../../experience-shell/queues/types";
import { optionDomId } from "./optionDomId";
import styles from "./RecipientField.module.css";

/**
 * RecipientField: a token field. Chips sit inside the field and the caret
 * follows the last chip. Typing filters the shared listbox; the keyboard
 * handles the rest (see `handleKeyDown`).
 *
 * Chips collapse to "+N" after four while the field does not have focus.
 */

// Chips visible in the resting state before the "+N" token.
const VISIBLE_CHIPS = 4;

export interface RecipientFieldProps {
  readonly inputId: string;
  readonly listId: string;
  readonly inputRef: RefObject<HTMLInputElement>;
  readonly frameRef: RefObject<HTMLDivElement>;
  readonly recipientIds: readonly string[];
  readonly labelById: ReadonlyMap<string, string>;
  readonly personalQueueIds: ReadonlySet<string>;
  readonly query: string;
  readonly onQueryChange: (query: string) => void;
  readonly listOpen: boolean;
  readonly onListOpenChange: (open: boolean) => void;
  readonly highlighted: QueueSummary | undefined;
  readonly onMoveHighlight: (delta: number) => void;
  readonly onAdd: (queue: QueueSummary) => void;
  readonly onRemove: (queueId: string) => void;
}

export function RecipientField({
  inputId,
  listId,
  inputRef,
  frameRef,
  recipientIds,
  labelById,
  personalQueueIds,
  query,
  onQueryChange,
  listOpen,
  onListOpenChange,
  highlighted,
  onMoveHighlight,
  onAdd,
  onRemove,
}: RecipientFieldProps): ReactElement {
  const [focusWithin, setFocusWithin] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const collapsed = !focusWithin && !expanded && recipientIds.length > VISIBLE_CHIPS;
  const visibleIds = collapsed ? recipientIds.slice(0, VISIBLE_CHIPS) : recipientIds;
  const hiddenCount = recipientIds.length - visibleIds.length;

  const focusInput = useCallback(() => {
    inputRef.current?.focus();
  }, [inputRef]);

  const handleBlur = (event: FocusEvent<HTMLDivElement>): void => {
    const next = event.relatedTarget;
    if (next instanceof Node && event.currentTarget.contains(next)) return;
    setFocusWithin(false);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        if (listOpen) onMoveHighlight(1);
        else onListOpenChange(true);
        return;
      case "ArrowUp":
        event.preventDefault();
        if (listOpen) onMoveHighlight(-1);
        return;
      case "Enter":
      case ",":
        if (listOpen && highlighted !== undefined) {
          event.preventDefault();
          onAdd(highlighted);
          return;
        }
        // Enter never submits from this field; "," has no meaning here.
        event.preventDefault();
        return;
      case "Tab":
        if (listOpen && highlighted !== undefined) {
          event.preventDefault();
          onAdd(highlighted);
        }
        return;
      case "Backspace": {
        if (query.length > 0 || recipientIds.length === 0) return;
        event.preventDefault();
        const last = recipientIds[recipientIds.length - 1];
        if (last !== undefined) onRemove(last);
        return;
      }
      case "Escape":
        if (!listOpen) return;
        // Consumed here so the dialog stays open.
        event.preventDefault();
        onListOpenChange(false);
        return;
      default:
        return;
    }
  };

  return (
    <div
      ref={frameRef}
      className={styles.frame}
      onClick={(event) => {
        if (event.target === event.currentTarget) focusInput();
      }}
      onFocus={() => {
        setFocusWithin(true);
      }}
      onBlur={handleBlur}
    >
      {visibleIds.map((id) => {
        const label = labelById.get(id) ?? id;
        const person = personalQueueIds.has(id);
        return (
          <span key={id} className={styles.chip} data-kind={person ? "person" : "queue"}>
            {person ? (
              <Avatar alt={label} size="xs" />
            ) : (
              <span aria-hidden="true" className={styles.chipGlyph}>
                <Layers size={12} strokeWidth={2} />
              </span>
            )}
            <Text
              as="span"
              role="bodySm"
              weight="medium"
              truncate
              className={clsx(styles.chipLabel)}
            >
              {label}
            </Text>
            <button
              type="button"
              aria-label={`Remove ${label}`}
              className={styles.remove}
              tabIndex={-1}
              onClick={() => {
                onRemove(id);
                focusInput();
              }}
            >
              <X size={12} strokeWidth={2.5} aria-hidden="true" />
            </button>
          </span>
        );
      })}
      {hiddenCount > 0 && (
        <button
          type="button"
          className={styles.overflow}
          aria-label={`Show ${String(hiddenCount)} more recipients`}
          onClick={() => {
            setExpanded(true);
            focusInput();
          }}
        >
          <Text as="span" role="bodySm" weight="medium" style={{ color: "inherit" }}>
            +{hiddenCount}
          </Text>
        </button>
      )}
      <TextInput
        id={inputId}
        ref={inputRef}
        variant="bare"
        className={styles.input}
        style={{ minHeight: 32, paddingBlock: 0 }}
        role="combobox"
        aria-expanded={listOpen}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={
          listOpen && highlighted !== undefined ? optionDomId(listId, highlighted.id) : undefined
        }
        placeholder={recipientIds.length === 0 ? "Person or queue\u2026" : ""}
        value={query}
        autoComplete="off"
        spellCheck={false}
        onChange={(event) => {
          const next = event.target.value;
          onQueryChange(next);
          if (next.length > 0 && !listOpen) onListOpenChange(true);
        }}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          if (listOpen) onListOpenChange(false);
        }}
      />
    </div>
  );
}
